/**
 * Visitor Utilities
 * 
 * Shared utilities for checking visitor status across web and plugin platforms.
 * Centralized to avoid code duplication.
 */

import { AppDispatch } from '../store';
import { authService } from '../api/services/auth.service';
import { setWallets, setAppLoading, setOnboardingCheck, setOnLandingFirstTime } from '../store/slices/appSlice';

/**
 * Check visitor status and update app state accordingly
 * 
 * This function:
 * - Checks if the user is a new visitor
 * - Updates wallets if provided
 * - Sets onLandingFirstTime flag if user is new
 * - Does NOT set onboarding state (onboarding is handled separately)
 * 
 * @param dispatch - Redux dispatch function
 * 
 * @example
 * ```ts
 * const dispatch = useAppDispatch();
 * await checkVisitor(dispatch);
 * ```
 */
export const checkVisitor = async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setAppLoading(true));

  try {
    console.log('👤 Checking visitor status...');
    const visitorResponse = await authService.checkVisitor();

    // Set wallets from visitor response
    if (visitorResponse.wallets) {
      dispatch(setWallets(visitorResponse.wallets));
    }

    // Set onLandingFirstTime flag if user is new
    if (visitorResponse.isNew === true) {
      dispatch(setOnLandingFirstTime(true));
    } else {
      dispatch(setOnLandingFirstTime(false));
    }

    // Mark onboarding check as complete
    dispatch(setOnboardingCheck(false));
  } catch (error) {
    console.error('Error checking visitor:', error);
    // Mark onboarding check as complete even on error
    dispatch(setOnboardingCheck(false));
    // On error, assume not first time
    dispatch(setOnLandingFirstTime(false));
  } finally {
    dispatch(setAppLoading(false));
  }
};

